// TanStack Query keys and hooks for the unsubscribe ladder. The `sender` SSE event (every state
// change) and the `work` event (the one-click POST's item) refresh these; see feedEvents.ts.
import { useQuery, type QueryClient } from "@tanstack/react-query";
import type { UnsubscribeView } from "@shared/api";
import { isApiError } from "./api";
import { unsubscribeApi } from "./unsubscribeApi";

export const unsubscribeKeys = {
  all: ["unsubscribe"] as const,
  view: (accountId: string, address: string) =>
    ["unsubscribe", accountId, address.toLowerCase()] as const,
  preview: (accountId: string, address: string, messageKey: string | null) =>
    ["unsubscribePreview", accountId, address.toLowerCase(), messageKey ?? ""] as const,
};

/** The ladder for one sender; null when MeeMail knows nothing about the address (404). */
export function useUnsubscribeView(
  accountId: string | null,
  address: string | null,
  enabled = true,
) {
  return useQuery({
    queryKey: unsubscribeKeys.view(accountId ?? "", address ?? ""),
    queryFn: async ({ signal }): Promise<UnsubscribeView | null> => {
      try {
        return await unsubscribeApi.view(accountId ?? "", address ?? "", signal);
      } catch (error) {
        if (isApiError(error) && error.status === 404) return null;
        throw error;
      }
    },
    enabled: enabled && !!accountId && !!address,
    staleTime: 20_000,
  });
}

/** The one-click preview, keyed by the message it is parsed from so a newer mail re-previews. */
export function useUnsubscribePreview(
  accountId: string,
  address: string,
  messageKey: string | null,
  enabled: boolean,
) {
  return useQuery({
    queryKey: unsubscribeKeys.preview(accountId, address, messageKey),
    queryFn: ({ signal }) => unsubscribeApi.preview(accountId, address, signal),
    enabled,
    retry: false,
    staleTime: 0,
    gcTime: 30_000,
  });
}

export function acceptUnsubscribeView(queryClient: QueryClient, view: UnsubscribeView): void {
  queryClient.setQueryData(unsubscribeKeys.view(view.accountId, view.address), view);
}

export function invalidateUnsubscribe(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: unsubscribeKeys.all });
}
